import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useOrderStats from '../hooks/useOrderStats';
import TrendCharts from './Dashboard/TrendCharts';
import RegionalAnalysis from './Dashboard/RegionalAnalysis';
import CustomReports from './Dashboard/CustomReports';
import SalesForecast from './Dashboard/SalesForecast';
import CancellationAnalysis from './Dashboard/CancellationAnalysis';
import CustomerSegmentation from './Dashboard/CustomerSegmentation';
import SocialMedia from './Dashboard/SocialMedia';
import DashboardSidebar from './Dashboard/DashboardSidebar';

// Composant pour les actions rapides
const QuickAction = ({ icon, title, description, onClick, color }) => (
  <button
    onClick={onClick}
    className={`block w-full p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow ${color}`}
  >
    <div className="flex items-start">
      <div className="flex-shrink-0">
        {icon}
      </div>
      <div className="ml-4">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>
    </div>
  </button>
);

// Composant pour les alertes
const Alert = ({ title, message, type }) => (
  <div className={`rounded-md p-4 ${
    type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
    type === 'error' ? 'bg-red-50 border-red-400' :
    'bg-blue-50 border-blue-400'
  } border`}>
    <div className="flex">
      <div className="flex-shrink-0">
        {type === 'warning' ? (
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : type === 'error' ? (
          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <div className="ml-3">
        <h3 className="text-sm font-medium text-gray-800">{title}</h3>
        <div className="mt-2 text-sm text-gray-600">{message}</div>
      </div>
    </div>
  </div>
);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopifyConnectSuccess, setShopifyConnectSuccess] = useState(false);
  const [shopifyInfo, setShopifyInfo] = useState(null);
  const stats = useOrderStats(orders);
  const [activeSection, setActiveSection] = useState('trend-charts');
  const sectionRefs = {
    'trend-charts': useRef(null),
    'regional-analysis': useRef(null),
    'custom-reports': useRef(null),
    'sales-forecast': useRef(null),
    'cancellation-analysis': useRef(null),
    'customer-segmentation': useRef(null),
    'social-media': useRef(null)
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId].current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Vérifier si on revient d'une redirection Shopify
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const auth = queryParams.get('auth');
    const token = queryParams.get('token');
    
    if (auth === 'success' && token) {
      console.log('Connexion Shopify réussie, nouveau token reçu');
      localStorage.setItem('token', token);
      setShopifyConnectSuccess(true);
      
      // Nettoyer l'URL
      navigate('/dashboard', { replace: true });
    } else if (auth === 'error') {
      const message = queryParams.get('message');
      setError(`Erreur de connexion Shopify: ${message || 'Erreur inconnue'}`);
      
      // Nettoyer l'URL
      navigate('/dashboard', { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    fetchOrders();
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/info`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      if (data.success) {
        console.log('Informations utilisateur:', data.user);
        setShopifyInfo(data.user);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des informations utilisateur:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setOrders(data.orders || []);
      } else {
        setError(data.error || 'Une erreur est survenue lors de la récupération des commandes');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des commandes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/orders/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        // Mettre à jour les commandes après la synchronisation
        fetchOrders();
      } else {
        setError(data.error || 'Une erreur est survenue lors de la synchronisation');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erreur lors de la synchronisation:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      {/* Sidebar */}
      <DashboardSidebar 
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
      />
      
      {/* Contenu principal */}
      <main className="flex-1 p-6 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Tableau de bord</h1>
            <div className="mt-4 md:mt-0 flex space-x-3">
              <button
                onClick={handleSyncOrders}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Synchronisation...' : 'Synchroniser les commandes'}
              </button>
              <Link
                to="/settings"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Paramètres
              </Link>
            </div>
          </div>
          
          {/* Afficher les messages de succès ou d'erreur */}
          {shopifyConnectSuccess && (
            <Alert
              type="info"
              title="Connexion Shopify réussie"
              message="Votre boutique Shopify a été connectée avec succès. Vous pouvez maintenant synchroniser vos commandes."
            />
          )}
          
          {error && (
            <Alert
              type="error"
              title="Erreur"
              message={error}
            />
          )}
          
          <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">Boutique Shopify</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Connectez une boutique Shopify à votre compte
                </p>
              </div>
              <Link 
                to="/connect-shopify" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Connecter une boutique
              </Link>
            </div>
          </div>
          
          {shopifyInfo && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Informations Shopify</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Boutique connectée</p>
                  <p className="font-medium">{shopifyInfo.shopifyShop || 'Non connecté'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  <p className="font-medium">
                    {shopifyInfo.shopifyConnected ? (
                      <span className="text-green-600">Connecté</span>
                    ) : shopifyInfo.shopifyPendingConnection ? (
                      <span className="text-yellow-600">En attente de connexion</span>
                    ) : (
                      <span className="text-red-600">Non connecté</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Statistiques */}
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Commandes</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats?.totalOrders || 0}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Chiffre d'affaires</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats && stats.totalRevenue ? (typeof stats.totalRevenue === 'number' ? `${stats.totalRevenue.toFixed(2)} €` : `${stats.totalRevenue} €`) : '0.00 €'}
                </dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Panier Moyen</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats && stats.averageOrderValue && typeof stats.averageOrderValue === 'number' ? `${stats.averageOrderValue.toFixed(2)} €` : '0.00 €'}
                </dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Taux de Conversion</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats && stats.conversionRate ? stats.conversionRate.toFixed(2) : '0.00'}%</dd>
              </div>
            </div>
          </div>
          
          {/* Sections d'analyse */}
          <div ref={sectionRefs['trend-charts']} className={`mt-8 ${activeSection === 'trend-charts' ? '' : 'hidden'}`}>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Tendances des ventes</h2>
            <TrendCharts orders={orders} />
          </div>
          
          <div ref={sectionRefs['regional-analysis']} className={`mt-8 ${activeSection === 'regional-analysis' ? '' : 'hidden'}`}>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Analyse régionale</h2>
            <RegionalAnalysis orders={orders} />
          </div>
          
          <div ref={sectionRefs['custom-reports']} className={`mt-8 ${activeSection === 'custom-reports' ? '' : 'hidden'}`}>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Rapports personnalisés</h2>
            <CustomReports orders={orders} />
          </div>
          
          <div ref={sectionRefs['sales-forecast']} className={`mt-8 ${activeSection === 'sales-forecast' ? '' : 'hidden'}`}>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Prévisions de ventes</h2>
            <SalesForecast orders={orders} />
          </div>
          
          <div ref={sectionRefs['cancellation-analysis']} className={`mt-8 ${activeSection === 'cancellation-analysis' ? '' : 'hidden'}`}>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Analyse des annulations</h2>
            <CancellationAnalysis orders={orders} />
          </div>
          
          <div ref={sectionRefs['customer-segmentation']} className={`mt-8 ${activeSection === 'customer-segmentation' ? '' : 'hidden'}`}>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Segmentation client</h2>
            <CustomerSegmentation orders={orders} />
          </div>
          
          <div ref={sectionRefs['social-media']} className={`mt-8 ${activeSection === 'social-media' ? '' : 'hidden'}`}>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Analyse des réseaux sociaux</h2>
            <SocialMedia orders={orders} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;